import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { Button, Dialog, IconButton, Input } from '../../ui'
import { Cancel, Delete, Share } from '@mui/icons-material'
import { useOrganizationUsers } from '../../../hooks/api/organizationHooks'
import { useForm, Controller } from 'react-hook-form'
import { useAuth } from '../../../hooks'
import { ClassNameValue } from 'tailwind-merge'
import cn from '../../../lib/utils/cn'
import { OrganizationUsersItem } from '../../../lib/api/types/organization.interfaces'

interface ShareUsersModalProps {
	isOpen: boolean
	onClose: () => void
	onAction: (users: OrganizationUsersItem[]) => void
	title?: string
	icon?: ReactNode
	initialUsersIds?: string[]
	isLoading?: boolean
	overlayClassName?: ClassNameValue
}

const ShareUsersModal: FC<ShareUsersModalProps> = ({
	isOpen,
	onClose,
	onAction,
	title,
	icon,
	initialUsersIds = [],
	isLoading,
	overlayClassName,
}) => {
	const { currentUser } = useAuth()

	const organizationId = currentUser?.organization?.id ?? ' '
	const [userSearch, setUserSearch] = useState('')

	const { data: usersData, isLoading: isLoadingUsers } = useOrganizationUsers(
		{ id: organizationId},
		{
			enabled: Boolean(organizationId && isOpen),
			staleTime: 1000 * 120,
		}
	)

	const { control, reset, setValue, watch } = useForm<{ userIds: string[] }>({
		defaultValues: {
			userIds: initialUsersIds ?? [],
		},
	})

	const { userIds } = watch()

	useEffect(() => {
		if (isOpen) {
			setValue('userIds', initialUsersIds)
		}
	}, [isOpen])

	const handleClose = () => {
		onClose()
		reset()
		setUserSearch('')
	}

	return (
		<Dialog
			isOpen={isOpen}
			onClose={handleClose}
			title={title ?? 'Share'}
			className="w-[500px] h-[382px] p-5 overflow-visible box-border"
			contentClassName="mt-4"
			overlayClassName={cn(overlayClassName)}
			icon={
				icon ?? (
					<Share className="fill-text-main dark:fill-text-main-dark w-6 h-6" />
				)
			}
			controllers={
				<div className="flex items-center justify-between gap-2 w-full">
					<Button
						variant="secondary"
						label="Clear"
						onClick={() => setValue('userIds', [])}
						className="flex items-center justify-center w-20"
					/>
					<div className="flex items-center justify-end w-1/3 gap-2">
						<Button
							variant="tertiary"
							label="Reset"
							onClick={() => {
								setValue('userIds', initialUsersIds)
							}}
							className="w-full flex items-center justify-center"
						/>
						<Button
							variant="primary"
							label="Save"
							onClick={async () => {
								// Convert userIds to user objects
								const selectedUsers: OrganizationUsersItem[] = userIds
									.map((userId) => {
										return usersData?.data?.entries?.find((u) => u.id === userId)
									})
									.filter((u): u is OrganizationUsersItem => Boolean(u))
								await onAction(selectedUsers)
							}}
							loading={isLoading}
							className="w-full flex items-center justify-center"
						/>
					</div>
				</div>
			}
		>
			<div>
				<Controller
					name="userIds"
					control={control}
					render={({ field: { value, onChange } }) => {
						const availableUsers = useMemo(() => {
							return (
								usersData?.data?.entries
									?.filter(
										(user) =>
											!user?.isDeactivated &&
											!value?.includes(user?.id) &&
											user?.id !== currentUser?.id &&
											(user?.name + user?.email)
												?.toLocaleLowerCase()
												?.includes(
													userSearch?.toLocaleLowerCase()?.trim() || ''
												)
									)
									.map((user) => ({
										id: user.id,
										label: user.name || user.email || '',
									})) || []
							)
						}, [usersData, value, userSearch])

						return (
							<div className="flex flex-col gap-2">
								<Input
									inputClassName="h-12 px-5 border-stroke-light dark:border-stroke-light-dark "
									labelClassName="font-normal"
									itemsWrapperClassName="shadow-xl"
									itemClassName="border-b border-stroke-light dark:border-stroke-light-dark  text-sm p-0 h-9 flex items-center px-4 last:border-b-0 odd:bg-background-light dark:odd:bg-background-dark-light font-normal"
									placeholder="Select users"
									withItems
									items={availableUsers}
									onItemClick={(item) => {
										const currentUsers = value || []
										if (!currentUsers.includes(item?.id)) {
											onChange([...currentUsers, item.id])
										}
									}}
									rightIcon={
										!!userSearch?.trim()?.length && (
											<Cancel
												className="fill-stroke-light hover:cursor-pointer default-transition hover:fill-text-secondary w-4 h-4"
												onClick={() => setUserSearch('')}
											/>
										)
									}
									value={userSearch}
									onChange={(e) => setUserSearch(e.target.value)}
								/>

								<div className="relative h-[178px] border border-stroke-light dark:border-stroke-light-dark  rounded-lg shadow-lg">
									<div className="bg-background-light h-[34px] flex items-center rounded-t-lg border-b border-stroke-light dark:border-stroke-light-dark dark:bg-background-light-dark">
										<p className="w-1/2 pl-5 flex items-center justify-start text-text-table dark:text-text-table-dark font-semibold text-sm">
											Name
										</p>
										<p className="w-1/2 pl-5 flex items-center justify-start text-text-table dark:text-text-table-dark font-semibold text-sm">
											Email
										</p>
									</div>

									<div className="flex flex-col box-border max-h-36 overflow-y-auto">
										{!value?.length && (
											<div className="text-stroke-light italic text-sm text-center h-36 w-full flex items-center justify-center">
												No users
											</div>
										)}
										{!isLoadingUsers &&
											value?.map((userId) => {
												const user = usersData?.data?.entries?.find(
													(u) => u.id === userId
												)
												if (!user) return null
												return (
													<ListUserItem
														key={userId}
														user={user}
														onRemove={() => {
															onChange(
																value.filter((id) => id !== userId)
															)
														}}
													/>
												)
											})}
									</div>
								</div>
							</div>
						)
					}}
				/>
			</div>
		</Dialog>
	)
}

interface ListUserItemProps {
	user: OrganizationUsersItem
	onRemove: () => void
	className?: ClassNameValue
	classNameItemName?: ClassNameValue
}

export const ListUserItem: FC<ListUserItemProps> = ({ user, onRemove, className, classNameItemName }) => {
	return (
		<div className={cn("flex items-center justify-between border-b last:border-0 border-stroke-light dark:border-stroke-light-dark min-h-12 h-12 box-border", className)}>
			<div className="flex w-1/2 items-center justify-start gap-2 pl-5 box-border">
				<img
					src={user?.avatar}
					className="w-8 h-8 rounded-lg"
					alt={user?.name || user?.email}
				/>
				<p className={cn("text-text-table dark:text-text-table-dark text-sm", classNameItemName)}>
					{user?.name || '-'}
				</p>
			</div>
			<div className="flex w-1/2 items-center justify-between box-border">
				<p className="text-text-table dark:text-text-table-dark text-sm pl-5 max-w-44 truncate">
					{user?.email}
				</p>

				<div className="px-5">
					<IconButton
						variant="secondary"
						className="w-8 h-8"
						icon={<Delete className="w-5 h-5" />}
						onClick={onRemove}
						tooltip={'Remove user'}
					/>
				</div>
			</div>
		</div>
	)
}
export default ShareUsersModal
